import type { ListParams } from '@resource-kit/model';
import type { ListResult } from './types';

/**
 * Server-mode "export all" pagination loop (#1459).
 *
 * `ResourcePage`'s CSV export offers "export all data", but the backend list
 * route is paginated — there is no bulk endpoint (deliberately: #1459 keeps
 * this purely front-end). This walks the resource's `list` adapter page by
 * page, reusing the operator's current search / filter / sort (everything on
 * `baseParams` except page/limit), until it has pulled every row.
 *
 * Kept as a pure function — no React, no toast — so the loop semantics are
 * unit-testable without rendering. The caller wraps rejections in a toast.
 */

/** Hard ceiling on a single "export all" pull, so a huge list can't lock the
 *  tab. Over this the loop throws before pulling everything. */
export const EXPORT_ALL_ROW_CAP = 10000;

/** Page size for the export pull — larger than the UI page size to minimize
 *  round-trips, bounded so a single response stays reasonable. */
export const EXPORT_ALL_PAGE_SIZE = 200;

interface FetchAllRowsOptions {
  /** Rows per request (default {@link EXPORT_ALL_PAGE_SIZE}). */
  pageSize?: number;
  /** Row ceiling (default {@link EXPORT_ALL_ROW_CAP}). */
  cap?: number;
  /** Builds the error thrown when `total` exceeds `cap`. Lets the caller
   *  supply a translated message. Defaults to a plain English Error. */
  tooLargeError?: (cap: number) => Error;
}

/**
 * `list` is the resource's own list adapter: `ResourceAPI.list`
 * (`packages/resource-kit-controllers/react/bundle.ts::ResourceAPI.list`) answers
 * `Promise<ListResult<T>>`, and this signature
 * is that same type. The loop itself only reads `items` and `total`.
 */
export async function fetchAllRowsServer<T>(
  list: (params: ListParams) => Promise<ListResult<T>>,
  baseParams: ListParams,
  options?: FetchAllRowsOptions,
): Promise<T[]> {
  const pageSize = options?.pageSize ?? EXPORT_ALL_PAGE_SIZE;
  const cap = options?.cap ?? EXPORT_ALL_ROW_CAP;

  const all: T[] = [];
  let page = 1;
  for (;;) {
    const response = await list({ ...baseParams, page, limit: pageSize });
    const total = response.total;
    if (total > cap) {
      throw options?.tooLargeError
        ? options.tooLargeError(cap)
        : new Error(`Too many rows to export (over ${cap}).`);
    }
    all.push(...response.items);
    // Stop once we've pulled the reported total, or when a page comes back
    // empty. A response that omits `total` disables TWO things at once, not
    // one: `all.length >= undefined` is false, so the row count alone would
    // never end the loop and the empty-page arm becomes the sole termination
    // backstop; and `undefined > cap` — the test just above in this function —
    // is false too, so `EXPORT_ALL_ROW_CAP` stops being the hard ceiling it is
    // described as there and the pull runs to exhaustion instead of throwing.
    if (all.length >= total || response.items.length === 0) break;
    page += 1;
  }
  return all;
}
